/**
 * The Today page's two pure rules: the status strip and the action list.
 *
 * Nothing here fetches or invents. The strip is the digest's funnel counts
 * with the prior-week comparison the digest already carries; the action list
 * is the growth rows and the ticket rows, ordered by how much a personal
 * note is worth right now, with "done today" read from what was actually
 * sent (or ticked off by hand in this browser).
 */
import type { GrowthPerson, ProductDigest, TicketRow } from './admin-data';
import { compareWarmFirst } from './growth-order';
import { hoursSince, localDayKey } from './format';

/* ── Status strip ───────────────────────────────────────────────────────── */

export interface StripRow {
  key: 'signups' | 'verified' | 'firstCheck' | 'thirdCheckDay' | 'paid';
  label: string;
  last24h: number;
  last7d: number;
  prior7d: number;
}

export const STRIP_LABEL: Record<StripRow['key'], string> = {
  signups: 'Signups',
  verified: 'Verified',
  firstCheck: 'First check',
  thirdCheckDay: 'Third day',
  paid: 'Paid',
};

export const statusStrip = (d: ProductDigest): StripRow[] =>
  (Object.keys(STRIP_LABEL) as StripRow['key'][]).map((key) => ({
    key,
    label: STRIP_LABEL[key],
    last24h: d.funnel.last24h[key],
    last7d: d.funnel.last7d[key],
    prior7d: d.funnel.prior7d[key],
  }));

/* ── Action list ────────────────────────────────────────────────────────── */

/** Days without a check after which somebody who was checking counts as stopped (the digest's rule). */
export const STALL_DAYS = 3;
/** An unverified account younger than this is still just signing up; older, it needs a nudge. */
export const UNVERIFIED_AFTER_HOURS = 24;

export type WriteReason = 'paid' | 'checking' | 'stopped' | 'never_checked' | 'unverified';

export const WRITE_REASON_LABEL: Record<WriteReason, string> = {
  paid: 'Paying',
  checking: 'Checking regularly',
  stopped: 'Stopped checking',
  never_checked: 'Never did a check',
  unverified: 'Not verified after a day',
};

/** Why this person is on the list today, in one line, so the note can open with it. */
export const WRITE_REASON_WHY: Record<WriteReason, string> = {
  paid: 'They paid. Thank them and ask what they want from it.',
  checking: 'They keep checking. Ask what they are noticing.',
  stopped: `They checked and then stopped for ${STALL_DAYS} days or more. Ask what got in the way.`,
  never_checked: 'They verified and never did a first check. Point at the camera step.',
  unverified: 'They typed their email and never confirmed it. One line, nothing else.',
};

export interface WriteItem { kind: 'write'; id: string; reason: WriteReason; person: GrowthPerson; done: boolean }
export interface TicketItem { kind: 'ticket'; id: string; ticket: TicketRow; done: boolean }
export interface CheckoutItem { kind: 'checkout'; id: string; person: GrowthPerson; done: boolean }
export type ActionItem = WriteItem | TicketItem | CheckoutItem;

const REASON_ORDER: WriteReason[] = ['paid', 'checking', 'stopped', 'never_checked', 'unverified'];

/**
 * Which group a person falls in today, or null when there is nothing to
 * write about yet (a fresh unverified account, or a checkout in progress,
 * which has its own list).
 */
export const writeReason = (p: GrowthPerson, now: number = Date.now()): WriteReason | null => {
  if (p.state === 'paid') return 'paid';
  if (p.state === 'abandoned_checkout') return null;
  if (!p.verified || p.state === 'unverified') {
    const h = hoursSince(p.createdAt, now);
    return h !== null && h >= UNVERIFIED_AFTER_HOURS ? 'unverified' : null;
  }
  const checks = Number.isFinite(p.checks) ? p.checks : 0;
  if (checks === 0) return 'never_checked';
  const sinceCheck = hoursSince(p.lastCheckAt, now);
  if (sinceCheck !== null && sinceCheck >= STALL_DAYS * 24) return 'stopped';
  return 'checking';
};

/** True when a note went out to this person today, in the viewer's zone. */
export const sentToday = (p: GrowthPerson, now: number = Date.now()): boolean => {
  const today = localDayKey(now);
  return p.outreach.some((o) => (o.status === 'sent' || o.status === 'queued') && localDayKey(new Date(o.at).getTime()) === today);
};

export interface ActionInput {
  people: GrowthPerson[];
  tickets: TicketRow[];
  /** Ids ticked off by hand today: write:<userId>, ticket:<id>, checkout:<userId>. */
  doneKeys: ReadonlySet<string>;
  now?: number;
}

/**
 * Everything worth doing today, most valuable first: notes to write (paid,
 * then checking, then stopped, then never checked, then unverified for a
 * day), then unanswered tickets oldest first, then checkouts that opened
 * and never paid. Done items keep their place and their flag; the page
 * decides how to fold them away.
 */
export const actionItems = (input: ActionInput): ActionItem[] => {
  const now = input.now ?? Date.now();
  const writes: WriteItem[] = [];
  const checkouts: CheckoutItem[] = [];
  for (const p of input.people) {
    if (p.state === 'abandoned_checkout') {
      const id = `checkout:${p.userId}`;
      checkouts.push({ kind: 'checkout', id, person: p, done: input.doneKeys.has(id) || sentToday(p, now) });
      continue;
    }
    const reason = writeReason(p, now);
    if (!reason) continue;
    const id = `write:${p.userId}`;
    writes.push({ kind: 'write', id, reason, person: p, done: input.doneKeys.has(id) || sentToday(p, now) });
  }
  writes.sort((a, b) => {
    const r = REASON_ORDER.indexOf(a.reason) - REASON_ORDER.indexOf(b.reason);
    return r !== 0 ? r : compareWarmFirst(a.person, b.person);
  });
  checkouts.sort((a, b) => compareWarmFirst(a.person, b.person));
  const tickets: TicketItem[] = input.tickets
    .filter((t) => t.status === 'new')
    .map((t) => ({ kind: 'ticket' as const, id: `ticket:${t.id}`, ticket: t, done: input.doneKeys.has(`ticket:${t.id}`) }))
    .sort((a, b) => b.ticket.ageHours - a.ticket.ageHours);
  return [...writes, ...tickets, ...checkouts];
};

/* ── Done today, kept in this browser ───────────────────────────────────── */

const doneStorageKey = (now: number) => `pv_admin_done_${localDayKey(now)}`;

export const readDoneKeys = (now: number = Date.now()): Set<string> => {
  try {
    const raw = localStorage.getItem(doneStorageKey(now));
    const list = raw ? (JSON.parse(raw) as unknown) : [];
    return new Set(Array.isArray(list) ? list.filter((x): x is string => typeof x === 'string') : []);
  } catch {
    return new Set();
  }
};

export const writeDoneKeys = (keys: ReadonlySet<string>, now: number = Date.now()): void => {
  try {
    localStorage.setItem(doneStorageKey(now), JSON.stringify(Array.from(keys)));
  } catch {
    /* storage unavailable: the tick lives in memory until the page closes */
  }
};
